// LeadTasksPanel — Phase 5.
//
// Auto-generated tasks for this lead. Not a full task manager — a
// lightweight "what should I do about this person right now" list
// that derives from the lead's own state. Every task traces to a
// signal on the lead payload.
//
// Signals:
//   - No contact in 5+ days                → Follow up call
//   - Follow-up date is today or overdue   → Follow up (scheduled)
//   - Birthday within 30 days              → Birthday reach-out
//   - Status='quoted' + no update in 3d    → Quote follow-up
//   - Status='sold' + no reward record     → Send thank-you + reward
//   - status='appt_set' + no confirmation  → Confirm appointment
//   - status='application' + >7d           → Poke on underwriting
//
// No new tables; no persistence layer for the tasks themselves —
// they regenerate every open, and completing one is a matter of
// updating the underlying lead state (which the drawer already
// supports via existing callbacks).

import React from 'react';

const DAY_MS = 86_400_000;

function daysUntilNext(dateStr) {
  if (!dateStr) return null;
  const t = Date.parse(dateStr);
  if (!Number.isFinite(t)) return null;
  return Math.floor((t - Date.now()) / DAY_MS);
}

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  const t = Date.parse(dateStr);
  if (!Number.isFinite(t)) return Infinity;
  return Math.floor((Date.now() - t) / DAY_MS);
}

function generateTasks(lead, callHistory, outreachLog) {
  const tasks = [];
  const lastCallAt   = callHistory[0]?.at || callHistory[0]?.created_at;
  const lastAnyTouch = [...(callHistory), ...(outreachLog)].sort((a, b) =>
    String(b.at || b.created_at || '').localeCompare(String(a.at || a.created_at || ''))
  )[0]?.at;

  const daysSinceTouch = daysSince(lastAnyTouch || lead?.updatedAt);

  // Follow-up date on the lead
  if (lead?.followUpDate) {
    const d = daysUntilNext(lead.followUpDate);
    if (d != null) {
      if (d < 0) tasks.push({ icon: '⏰', priority: 1, text: `Follow-up was ${Math.abs(d)}d ago`, action: 'Call now' });
      else if (d === 0) tasks.push({ icon: '⏰', priority: 1, text: 'Follow-up scheduled for today', action: 'Reach out' });
      else if (d <= 3)  tasks.push({ icon: '⏰', priority: 2, text: `Follow-up in ${d}d`, action: 'Prep for the call' });
    }
  }

  // Stale lead
  if (daysSinceTouch >= 5 && daysSinceTouch !== Infinity) {
    tasks.push({ icon: '📞', priority: 2, text: `No touch in ${daysSinceTouch}d`, action: 'Send SMS' });
  }
  if (!lastCallAt && daysSinceTouch !== Infinity) {
    tasks.push({ icon: '📞', priority: 2, text: 'Never called yet', action: 'First call' });
  }

  // Birthday
  const birthday = lead?.birthday || lead?.dob;
  if (birthday) {
    const bd = new Date(birthday);
    const today = new Date();
    const nextBd = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
    if (nextBd < today) nextBd.setFullYear(today.getFullYear() + 1);
    const dTo = Math.ceil((nextBd - today) / DAY_MS);
    if (dTo >= 0 && dTo <= 30) {
      tasks.push({ icon: '🎂', priority: 3, text: `Birthday in ${dTo}d`, action: 'Send birthday note' });
    }
  }

  // Status-based
  if (lead?.status === 'quoted' && daysSinceTouch >= 3) {
    tasks.push({ icon: '📄', priority: 1, text: `Quoted ${daysSinceTouch}d ago — no update`, action: 'Follow up on quote' });
  }
  if (lead?.status === 'appt_set' && daysSinceTouch >= 1) {
    tasks.push({ icon: '📅', priority: 1, text: 'Appointment set — confirm 24h prior', action: 'Send confirmation' });
  }
  if (lead?.status === 'application' && daysSinceTouch >= 7) {
    tasks.push({ icon: '📝', priority: 2, text: 'Application in flight >7d', action: 'Check underwriting' });
  }
  if (lead?.status === 'sold' && !lead?.rewardSent) {
    tasks.push({ icon: '🎁', priority: 2, text: 'Sold — send thank-you + ask for referral', action: 'Send thank-you' });
  }

  // Missing profile signals — coach the operator to fill Phase 2 fields.
  const p = lead?.insurance_profile || {};
  if (lead?.vertical === 'health' && !p.health?.primary_doctor) {
    tasks.push({ icon: '🩺', priority: 3, text: 'No primary doctor on file', action: 'Ask + record' });
  }
  if (lead?.vertical === 'life' && !p.life?.smoker) {
    tasks.push({ icon: '🚭', priority: 3, text: 'Smoker status unknown', action: 'Ask + record' });
  }

  return tasks.sort((a, b) => a.priority - b.priority).slice(0, 6);
}

function PriorityDot({ p }) {
  const c = p === 1 ? '#dc2626' : p === 2 ? '#d97706' : '#64748b';
  return <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: c }}/>;
}

export default function LeadTasksPanel({ lead, callHistory = [], outreachLog = [] }) {
  const tasks = generateTasks(lead, callHistory, outreachLog);
  if (tasks.length === 0) return null;

  return (
    <div style={{
      background: '#fff7ed',
      border: '1px solid #fed7aa',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#c2410c', fontWeight: 800 }}>
          Auto Tasks
        </div>
        <div style={{ fontSize: 11, color: '#9a3412' }}>
          {tasks.length} suggested for this lead
        </div>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {tasks.map((t, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#ffffff', border: '1px solid #fed7aa',
            borderRadius: 6, padding: '6px 10px',
          }}>
            <PriorityDot p={t.priority}/>
            <div style={{ fontSize: 15 }}>{t.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#9a3412', fontWeight: 700 }}>{t.text}</div>
            </div>
            <div style={{
              fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase',
              color: '#c2410c', fontWeight: 800,
              background: '#fff7ed', border: '1px solid #fed7aa',
              padding: '3px 10px', borderRadius: 999,
            }}>{t.action}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
